import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  Typography,
} from "@mui/material";
import axios from "axios";
import CreditsBanner from "../Components/CreditsNudge";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import YouTubeChannelBackupContent from "../Contents/YtChannelBackupContent";
import { message } from "../Constants/message.js";
import CommentSection from "../Components/CommentSection.js";

function YouTubeChannelBackup({ user, login, isLoggedIn }) {
  const [channelUrl, setChannelUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });
  const [downloadUrl, setDownloadUrl] = useState("");

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    if (primaryInputText) {
      setChannelUrl(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
  }, []);

  const handleBackupDownload = async () => {
    setError("");
    setSuccess("");
    setDownloadUrl("");

    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }

    if (credits.remaining <= 0) {
      setError("Not enough credits to export your YouTube channel backup.");
      return;
    }
    if (!isLoggedIn) {
      setError("You're not logged in. Please log in to download channel data.");
      login();
      return;
    }

    if (!channelUrl.trim()) {
      setError(
        "Please enter a valid YouTube video URL. It should be like https://www.youtube.com/watch?v=VIDEO_ID"
      );
      return;
    }
    // ✅ Basic YouTube URL validation
    const youtubeRegex =
      /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/(watch\?v=|embed\/|shorts\/)?([a-zA-Z0-9_-]{11})/;

    if (!youtubeRegex.test(channelUrl)) {
      setError(
        "Please enter a valid YouTube video URL not the channel URL/link. Give any public video link from your channel to take entire channel backup. It should be like https://www.youtube.com/watch?v=VIDEO_ID"
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/channelBackUp`,
        { channelUrl },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      const fileName = response.data.fileName;
      setSuccess(response.data.successMessage);

      // Fetch updated credits
      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );

      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });

      // Download the file automatically
      const downloadResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/channelBackUp/download`,
        { fileName },
        {
          withCredentials: true,
          responseType: "blob",
        }
      );

      const blob = new Blob([downloadResponse.data], {
        type: "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadUrl(url);
    } catch (err) {
      setError(
        err.response?.data?.errorMessage ||
          "Failed to get channel data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            Backup YouTube Channel Data 💾
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Enter any <strong>YouTube video URL</strong> from the channel you
            want to back up — <br />
            <em>
              ⚠️ Not the channel URL, just any video link from that channel.
            </em>
          </SubtitleText>

          <Box mb={3}>
            <TextField
              fullWidth
              label="YouTube Video URL"
              variant="outlined"
              value={channelUrl}
              onChange={(e) => setChannelUrl(e.target.value)}
              placeholder="e.g. https://www.youtube.com/watch?v=xyz123"
              size="medium"
              id="youtube-channel-url"
              name="primary-textbox"
            />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 1 }}
          >
            📌 Please enter any <strong>YouTube video URL</strong> from the
            channel — not the channel URL.
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 1 }}
          >
            🚀 <strong>Free users</strong> can export up to{" "}
            <strong>10 videos</strong> per channel. <br />
            🔓 <strong>Paid users</strong> can back up up to{" "}
            <strong>500 videos</strong> per channel.
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 2 }}
          >
            💳 <strong>1 export = 1 credit</strong>
          </Typography>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
              {downloadUrl && (
                <>
                  <br />
                  If your download hasn’t started,{" "}
                  <a href={downloadUrl} download="channel_backup.csv">
                    click here
                  </a>
                  .
                </>
              )}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleBackupDownload}
            >
              Get Channel Backup
            </StyledButton>
          </Box>
        </ContentBox>
        <CommentSection
          toolId="youtube/channelBackUp"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <YouTubeChannelBackupContent />
      </PageContainer>
    </>
  );
}

export default YouTubeChannelBackup;
